import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, Marker, useJsApiLoader, StandaloneSearchBox, Libraries, Circle, Polygon } from '@react-google-maps/api';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { MapPin, FileText, CheckCircle2, Circle as CircleIcon, Hexagon, Undo2, Trash2, HelpCircle, AlertTriangle } from 'lucide-react';
import { useActiveRole } from '@/hooks/useActiveRole';
import { useZoneMapControls } from '@/hooks/useZoneMapControls';
import { IZoneForm, IZoneDetailResponse } from '@/interfaces/Zone/zone.interface';

const GOOGLE_API_KEY = import.meta.env.VITE_APP_GOOGLE_API_KEY || '';

const defaultLocation = {
    lat: -26.204103,
    lng: 28.047305
};

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '12px'
};

const LIBRARIES: Libraries = ['places'];

export const AddZone: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { prefix } = useActiveRole();

    const [loadingData, setLoadingData] = useState(isEditMode);

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors, isSubmitting },
        watch,
    } = useForm<IZoneForm>({
        defaultValues: {
            status: 'ACTIVE',
            latitude: defaultLocation.lat,
            longitude: defaultLocation.lng,
            radius: 1000,
            zoneType: 'radius',
            polygon: [],
        }
    });
    const radius = watch('radius');
    const zoneType = watch('zoneType') || 'radius';
    const polygon = watch('polygon') || [];
    const {
        markerPosition,
        setMarkerPosition,
        polygonError,
        onCircleRadiusChanged,
        onCircleLoad,
        onCircleUnmount,
        onPolygonLoad,
        onPolygonUnmount,
        onMapClick,
        onMarkerDragEnd,
        onVertexDrag,
        onLoadSearchBox,
        onPlacesChanged,
        reverseGeocode,
        getPolygonCentroid
    } = useZoneMapControls(zoneType, polygon, setValue, defaultLocation);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_API_KEY,
        libraries: LIBRARIES
    });

    useEffect(() => {
        const fetchZone = async () => {
            if (!id) return;
            try {
                const response = await callApi<IZoneDetailResponse>(
                    `${API_URL}${API_ENDPOINT.zones}/${id}`,
                    { method: 'GET' }
                );
                const zone = response?.data;
                const lat = zone.latitude ? parseFloat(zone.latitude.toString()) : defaultLocation.lat;
                const lng = zone.longitude ? parseFloat(zone.longitude.toString()) : defaultLocation.lng;

                let fetchedCoords: { lat: number; lng: number }[] = [];
                if (zone.polygon) {
                    if (Array.isArray(zone.polygon)) {
                        fetchedCoords = zone.polygon.map((c: any) => ({
                            lat: Number(c.lat || c.latitude),
                            lng: Number(c.lng || c.longitude)
                        }));
                    } else if (typeof zone.polygon === 'string') {
                        try {
                            const parsed = JSON.parse(zone.polygon);
                            if (Array.isArray(parsed)) {
                                fetchedCoords = parsed.map((c: any) => ({
                                    lat: Number(c.lat || c.latitude),
                                    lng: Number(c.lng || c.longitude)
                                }));
                            }
                        } catch (e) {
                            console.error("Failed parsing polygon from DB string", e);
                        }
                    }
                }

                const fetchedZoneType = zone.zoneType || (fetchedCoords.length > 0 ? 'polygon' : 'radius');

                if (fetchedCoords.length > 0) {
                    const centroid = getPolygonCentroid(fetchedCoords);
                    setMarkerPosition(centroid);
                } else {
                    setMarkerPosition({ lat, lng });
                }

                reset({
                    name: zone.name || '',
                    description: zone.description || '',
                    city: zone.city || '',
                    area: zone.area || '',
                    latitude: lat,
                    longitude: lng,
                    radius: zone.radius ? Number(zone.radius) : 1000,
                    status: zone.status || 'ACTIVE',
                    zoneType: fetchedZoneType,
                    polygon: fetchedCoords,
                });
            } catch (error) {
                ThrowError(error);
                navigate(-1);
            } finally {
                setLoadingData(false);
            }
        };

        if (isEditMode) {
            fetchZone();
        } else {
            setValue('latitude', defaultLocation.lat, { shouldValidate: true });
            setValue('longitude', defaultLocation.lng, { shouldValidate: true });
        }
    }, [id, isEditMode, reset, setValue, navigate]);

    const onSubmit = async (data: IZoneForm) => {
        if (data.zoneType === 'polygon' && (!data.polygon || data.polygon.length < 4)) {
            showToastNotification('danger', 'Please define at least 4 points on the map to create a custom boundary zone.');
            return;
        }

        try {
            const url = isEditMode
                ? `${API_URL}${API_ENDPOINT.zones}/${id}`
                : `${API_URL}${API_ENDPOINT.zones}`;

            const method = isEditMode ? 'PATCH' : 'POST';

            const payload = {
                name: data.name?.trim(),
                description: data.description?.trim(),
                city: data.city?.trim(),
                area: data.area?.trim(),
                latitude: data.latitude,
                longitude: data.longitude,
                ...(zoneType == "polygon" ? { polygon: data.polygon } : { radius: data.radius }),
                type: data.zoneType,
            };

            const response = await callApi<any>(url, { method, data: payload });

            showToastNotification('success', response?.message || `Zone ${isEditMode ? 'updated' : 'created'} successfully.`);
            navigate(prefix + '/zones');
        } catch (error) {
            ThrowError(error);
        }
    };

    if (loadingData) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
                </div>
            </Layout>
        );
    }


    return (
        <Layout>
            <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                <MainHeading
                    title={isEditMode ? "Edit Zone" : "Create New Zone"}
                    description={isEditMode ? "Update the zone's details and spatial location." : "Define a new geographic zone and its operational boundaries."}
                    showBack
                    onBack={() => navigate(-1)}
                />

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* ── Zone Definition Method Selector ── */}
                    <SectionCard title="Zone Definition Method" icon={<HelpCircle className="w-5 h-5 text-muted-foreground" />}>
                        <p className="text-xs text-muted-foreground mb-4 font-medium">
                            Choose how you would like to define the boundaries of this zone.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Radius Card */}
                            <button
                                type="button"
                                onClick={() => setValue('zoneType', 'radius')}
                                className={`flex items-start gap-4 p-4 rounded-xl border text-left transition-all duration-200 ${zoneType === 'radius'
                                    ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-sm'
                                    : 'border-border bg-card hover:bg-muted/50 cursor-pointer'
                                    }`}
                            >
                                <div className={`p-3 rounded-lg shrink-0 transition-colors ${zoneType === 'radius' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                                    }`}>
                                    <CircleIcon className="w-6 h-6" />
                                </div>
                                <div className="space-y-1">
                                    <h4 className="font-semibold text-sm text-foreground">Radius-Based (Circle)</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        Define the zone with a center coordinate and a circle radius in meters. Quick and simple.
                                    </p>
                                </div>
                            </button>

                            {/* Coordinates Card */}
                            <button
                                type="button"
                                onClick={() => setValue('zoneType', 'polygon')}
                                className={`flex items-start gap-4 p-4 rounded-xl border text-left transition-all duration-200 ${zoneType === 'polygon'
                                    ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-sm'
                                    : 'border-border bg-card hover:bg-muted/50 cursor-pointer'
                                    }`}
                            >
                                <div className={`p-3 rounded-lg shrink-0 transition-colors ${zoneType === 'polygon' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                                    }`}>
                                    <Hexagon className="w-6 h-6" />
                                </div>
                                <div className="space-y-1">
                                    <h4 className="font-semibold text-sm text-foreground">Coordinate-Based (Polygon)</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        Draw a custom boundary by placing points on the map. Best for precise, irregular service areas.
                                    </p>
                                </div>
                            </button>
                        </div>
                    </SectionCard>

                    {/* ── Spatial Data & Location Card ── */}
                    <SectionCard title="Spatial Data & Location" icon={<MapPin className="w-5 h-5 text-muted-foreground" />}>
                        <p className="text-xs text-muted-foreground mb-4 font-medium leading-relaxed">
                            {zoneType === 'radius'
                                ? "Search for an address or move the map marker to set the central coordinates for this zone. Drag the circle edge to adjust radius."
                                : "Search for a starting location, then click directly on the map to define the custom boundaries (vertices) of your zone. You must place at least 4 points."}
                        </p>

                        {isLoaded && (
                            <div className="mb-4">
                                <StandaloneSearchBox
                                    onLoad={onLoadSearchBox}
                                    onPlacesChanged={onPlacesChanged}
                                >
                                    <input
                                        type="text"
                                        placeholder="Search for a location to center the map..."
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-shadow duration-150 animate-in fade-in duration-300"
                                    />
                                </StandaloneSearchBox>
                            </div>
                        )}

                        <div className="overflow-hidden border border-secondary/20 rounded-xl bg-secondary/5 mb-4">
                            {isLoaded ? (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={markerPosition}
                                    zoom={14}
                                    options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false }}
                                    onClick={onMapClick}
                                >
                                    {/* Radius Mode Components */}
                                    {zoneType === 'radius' && (
                                        <>
                                            <Marker
                                                position={markerPosition}
                                                draggable={true}
                                                onDragEnd={onMarkerDragEnd}
                                                animation={google.maps.Animation.DROP}
                                            />
                                            {radius && radius > 0 && (
                                                <Circle
                                                    onLoad={onCircleLoad}
                                                    onUnmount={onCircleUnmount}
                                                    onRadiusChanged={onCircleRadiusChanged}
                                                    center={markerPosition}
                                                    radius={Number(radius)}
                                                    options={{
                                                        fillColor: '#0ea5e9',
                                                        fillOpacity: 0.2,
                                                        strokeColor: '#0284c7',
                                                        strokeOpacity: 0.9,
                                                        strokeWeight: 3,
                                                        clickable: false,
                                                        editable: true,
                                                        zIndex: 1
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}

                                    {/* Coordinates Mode Components */}
                                    {zoneType === 'polygon' && (
                                        <>
                                            {/*
                                             * IMPORTANT: We intentionally do NOT use editable/draggable on
                                             * the Google Maps Polygon. When editable:true is set, Google Maps
                                             * renders the drag movement directly on its WebGL canvas in real-time.
                                             * No React listener (set_at, mouseup, etc.) fires fast enough to
                                             * intercept or correct a self-intersection mid-drag.
                                             *
                                             * Instead: each vertex is a separate draggable <Marker>.
                                             * onDragEnd fires AFTER the user releases, giving us the final
                                             * position. We then recompute the convex hull and update the
                                             * polygon — making overlapping lines geometrically impossible.
                                             */}
                                            {polygon.map((coord, index) => (
                                                <Marker
                                                    key={`vertex-${index}`}
                                                    position={coord}
                                                    draggable
                                                    onDragEnd={(e) => onVertexDrag(index, e)}
                                                    label={{
                                                        text: (index + 1).toString(),
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        fontSize: '12px'
                                                    }}
                                                    options={{ zIndex: 10 }}
                                                />
                                            ))}

                                            {polygon.length >= 3 && (
                                                <Polygon
                                                    paths={polygon}
                                                    onLoad={onPolygonLoad}
                                                    onUnmount={onPolygonUnmount}
                                                    options={{
                                                        fillColor: '#10b981',
                                                        fillOpacity: 0.25,
                                                        strokeColor: '#059669',
                                                        strokeOpacity: 0.9,
                                                        strokeWeight: 3,
                                                        clickable: false,
                                                        editable: false,
                                                        draggable: false,
                                                        zIndex: 1
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </GoogleMap>
                            ) : (
                                <div className="flex items-center justify-center w-full h-[400px] bg-secondary/10">
                                    <span className="text-sm text-muted-foreground animate-pulse">Loading Google Maps...</span>
                                </div>
                            )}
                        </div>

                        {/* Polygon Error Warning */}
                        {polygonError && zoneType === 'polygon' && (
                            <div className="mb-4 flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 px-4 py-3 rounded-xl animate-in slide-in-from-top-2 font-medium">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                <p>{polygonError}</p>
                            </div>
                        )}

                        {/* Coordinates Helper Controls */}
                        {zoneType === 'polygon' && (
                            <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl border border-dashed border-border bg-secondary/5 transition-all duration-300 animate-in slide-in-from-top duration-300">
                                <div className="text-xs text-muted-foreground flex items-center gap-2 font-medium">
                                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                                    <span>
                                        {polygon.length === 0
                                            ? "Click directly on the map above to place the first point of your zone."
                                            : polygon.length === 1
                                                ? "Placed 1 point. Click again to place the second point."
                                                : polygon.length === 2
                                                    ? "Placed 2 points. Click again to complete the polygon shape."
                                                    : `${polygon.length} vertices defined. Drag any vertex handle to fine-tune the boundaries.`
                                        }
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={polygon.length === 0}
                                        onClick={() => {
                                            const updated = polygon.slice(0, -1);
                                            setValue('polygon', updated, { shouldValidate: true });
                                            const centroid = getPolygonCentroid(updated);
                                            setValue('latitude', centroid.lat, { shouldValidate: true });
                                            setValue('longitude', centroid.lng, { shouldValidate: true });
                                            if (updated.length > 0) {
                                                reverseGeocode(centroid.lat, centroid.lng);
                                            }
                                        }}
                                        leftIcon={<Undo2 className="w-3.5 h-3.5" />}
                                    >
                                        Undo Last
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="text-destructive hover:bg-destructive/10 hover:text-destructive transition-colors duration-150 border-destructive/20 hover:border-destructive/30 cursor-pointer"
                                        disabled={polygon.length === 0}
                                        onClick={() => {
                                            setValue('polygon', [], { shouldValidate: true });
                                        }}
                                        leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                            </div>
                        )}
                    </SectionCard>

                    {/* ── Zone Details Card ── */}
                    <SectionCard title="Zone Details" icon={<FileText className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                label="Zone Name"
                                requiredIcon
                                placeholder="e.g. North District Zone"
                                error={errors.name?.message}
                                {...register('name', { ...errorTypes.required('Zone Name') })}
                            />

                            <FormField
                                label="City"
                                placeholder="e.g. Johannesburg"
                                error={errors.city?.message}
                                {...register('city')}
                            />
                            <FormField
                                label="Area / Suburb"
                                placeholder="e.g. Sandton"
                                error={errors.area?.message}
                                {...register('area')}
                            />
                            <FormField
                                label="Description"
                                type="textarea"
                                rows={2}
                                placeholder="Brief description of this operational zone..."
                                error={errors.description?.message}
                                {...register('description')}
                            />

                            {zoneType === 'radius' && (
                                <FormField
                                    label="Zone Radius (meters)"
                                    type="number"
                                    requiredIcon
                                    placeholder="e.g. 1000"
                                    error={errors.radius?.message}
                                    {...register('radius', {
                                        required: zoneType === 'radius' ? 'Radius is required' : false,
                                        min: { value: 1, message: 'Radius must be at least 1 meter' }
                                    })}
                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                />
                            )}
                        </div>
                    </SectionCard>

                    {/* ── Submit Buttons ── */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-dashed">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            leftIcon={<CheckCircle2 className="w-4 h-4" />}
                            isLoading={isSubmitting}
                            loadingText={isEditMode ? "Updating Zone..." : "Creating Zone..."}
                        >
                            {isEditMode ? "Save Changes" : "Create Zone"}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};

export default AddZone;
